import React, {useState, useEffect, useRef} from 'react';
import {Text, Button, Group, Stack, Card, Box, Progress, Badge} from '@mantine/core';
import {IconMicrophone, IconMicrophoneOff, IconGauge} from '@tabler/icons-react';
import springboard from 'springboard';

declare module 'springboard/module_registry/module_registry' {
    interface AllModules {
        GuitarTuner: GuitarTunerModule;
    }
}

type GuitarTunerModule = {
    TunerForm: React.ComponentType<{close: () => void}>;
};

const GUITAR_STRINGS = [
    {name: 'E6', frequency: 82.41, string: 6},
    {name: 'A5', frequency: 110.00, string: 5},
    {name: 'D4', frequency: 146.83, string: 4},
    {name: 'G3', frequency: 196.00, string: 3},
    {name: 'B2', frequency: 246.94, string: 2},
    {name: 'E1', frequency: 329.63, string: 1},
];

const CHROMATIC_NOTES = [
    'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'
];

function getFrequencyForNote(note: string, octave: number): number {
    const noteIndex = CHROMATIC_NOTES.indexOf(note);
    if (noteIndex === -1) return 0;
    
    const A4_FREQUENCY = 440;
    const A4_INDEX = 9;
    const A4_OCTAVE = 4;
    
    const semitonesFromA4 = (octave - A4_OCTAVE) * 12 + (noteIndex - A4_INDEX);
    return A4_FREQUENCY * Math.pow(2, semitonesFromA4 / 12);
}

function getClosestNote(frequency: number): {note: string, octave: number, frequency: number} {
    const A4_FREQUENCY = 440;
    const semitonesFromA4 = Math.round(12 * Math.log2(frequency / A4_FREQUENCY));
    
    const octave = Math.floor((semitonesFromA4 + 9) / 12) + 4;
    const noteIndex = ((semitonesFromA4 + 9) % 12 + 12) % 12;
    const note = CHROMATIC_NOTES[noteIndex];
    const exactFrequency = getFrequencyForNote(note, octave);
    
    return {note, octave, frequency: exactFrequency};
}

type TunerState = {
    isListening: boolean;
    currentFrequency: number | null;
    detectedNote: {note: string, octave: number, frequency: number} | null;
    closestString: typeof GUITAR_STRINGS[0] | null;
    centOffset: number;
    isInTune: boolean;
};

function autocorrelate(buffer: Float32Array, sampleRate: number): number {
    const bufferSize = buffer.length;
    const autocorrelation = new Float32Array(bufferSize);
    
    for (let lag = 0; lag < bufferSize; lag++) {
        let sum = 0;
        for (let index = 0; index < bufferSize - lag; index++) {
            sum += buffer[index] * buffer[index + lag];
        }
        autocorrelation[lag] = sum;
    }
    
    let maxCorrelation = -1;
    let bestOffset = -1;
    
    const minPeriod = Math.floor(sampleRate / 800);
    const maxPeriod = Math.floor(sampleRate / 60);
    
    for (let offset = minPeriod; offset < maxPeriod; offset++) {
        if (autocorrelation[offset] > maxCorrelation) {
            maxCorrelation = autocorrelation[offset];
            bestOffset = offset;
        }
    }
    
    if (bestOffset === -1) return -1;
    
    return sampleRate / bestOffset;
}

function findClosestString(frequency: number) {
    let closest = GUITAR_STRINGS[0];
    let minDiff = Math.abs(frequency - closest.frequency);
    
    for (const string of GUITAR_STRINGS) {
        const diff = Math.abs(frequency - string.frequency);
        if (diff < minDiff) {
            minDiff = diff;
            closest = string;
        }
    }
    
    return closest;
}

function getCentOffset(frequency: number, targetFrequency: number): number {
    return Math.round(1200 * Math.log2(frequency / targetFrequency));
}

function TunerForm({close}: {close: () => void}) {
    const [tunerState, setTunerState] = useState<TunerState>({
        isListening: false,
        currentFrequency: null,
        detectedNote: null,
        closestString: null,
        centOffset: 0,
        isInTune: false,
    });
    
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const animationRef = useRef<number | undefined>(undefined);
    const frequencyBufferRef = useRef<number[]>([]);
    const lastUpdateTimeRef = useRef<number>(0);
    
    const startListening = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            streamRef.current = stream;
            
            const audioContext = new AudioContext();
            const analyser = audioContext.createAnalyser();
            const microphone = audioContext.createMediaStreamSource(stream);
            
            analyser.fftSize = 2048;
            microphone.connect(analyser);
            
            audioContextRef.current = audioContext;
            analyserRef.current = analyser;
            
            setTunerState(prev => ({...prev, isListening: true}));
            
            const processAudio = () => {
                if (!analyserRef.current) return;

                const bufferLength = analyserRef.current.frequencyBinCount;
                const dataArray = new Float32Array(bufferLength);
                analyserRef.current.getFloatTimeDomainData(dataArray);

                const frequency = autocorrelate(dataArray, audioContext.sampleRate);

                if (frequency > 0 && frequency < 800) {
                    frequencyBufferRef.current.push(frequency);
                    if (frequencyBufferRef.current.length > 5) {
                        frequencyBufferRef.current.shift();
                    }

                    const now = Date.now();
                    if (now - lastUpdateTimeRef.current > 100) {
                        const smoothedFrequency = frequencyBufferRef.current.reduce((a, b) => a + b, 0) / frequencyBufferRef.current.length;

                        const detectedNote = getClosestNote(smoothedFrequency);
                        const closestString = findClosestString(smoothedFrequency);
                        const centOffset = getCentOffset(smoothedFrequency, detectedNote.frequency);
                        const isInTune = Math.abs(centOffset) <= 5;

                        setTunerState(prev => ({
                            ...prev,
                            currentFrequency: smoothedFrequency,
                            detectedNote,
                            closestString,
                            centOffset,
                            isInTune,
                        }));

                        lastUpdateTimeRef.current = now;
                    }
                }

                animationRef.current = requestAnimationFrame(processAudio);
            };
            
            processAudio();
        } catch (error) {
            console.error('Error accessing microphone:', error);
        }
    };
    
    const stopListening = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track: MediaStreamTrack) => track.stop());
            streamRef.current = null;
        }
        
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        
        analyserRef.current = null;
        frequencyBufferRef.current = [];
        lastUpdateTimeRef.current = 0;

        setTunerState({
            isListening: false,
            currentFrequency: null,
            detectedNote: null,
            closestString: null,
            centOffset: 0,
            isInTune: false,
        });
    };
    
    useEffect(() => {
        return () => {
            stopListening();
        };
    }, []);
    
    const getTunerColor = () => {
        if (!tunerState.detectedNote) return 'gray';
        if (tunerState.isInTune) return 'green';
        return Math.abs(tunerState.centOffset) > 20 ? 'red' : 'yellow';
    };
    
    const getNeedlePosition = () => {
        if (!tunerState.detectedNote) return 50;
        const maxCents = 50;
        const clampedOffset = Math.max(-maxCents, Math.min(maxCents, tunerState.centOffset));
        return 50 + (clampedOffset / maxCents) * 50;
    };
    
    return (
        <Stack gap="lg">
                
                <Card padding="lg" radius="md" withBorder>
                    <Stack gap="md">
                        <Group justify="center">
                            <Button
                                onClick={tunerState.isListening ? stopListening : startListening}
                                variant={tunerState.isListening ? "filled" : "outline"}
                                color={tunerState.isListening ? "red" : "blue"}
                                leftSection={
                                    tunerState.isListening ? 
                                    <IconMicrophoneOff size={16} /> : 
                                    <IconMicrophone size={16} />
                                }
                            >
                                {tunerState.isListening ? 'Stop Tuning' : 'Start Tuning'}
                            </Button>
                        </Group>
                        
                        {tunerState.isListening && (
                            <>
                                <Box ta="center">
                                    {tunerState.detectedNote ? (
                                        <Stack gap="sm">
                                            <Text size="xl" fw={700}>
                                                {tunerState.detectedNote.note}{tunerState.detectedNote.octave}
                                            </Text>
                                            <Text size="sm" c="dimmed">
                                                Target: {tunerState.detectedNote.frequency.toFixed(2)} Hz
                                            </Text>
                                            {tunerState.currentFrequency && (
                                                <Text size="sm">
                                                    Current: {tunerState.currentFrequency.toFixed(2)} Hz
                                                </Text>
                                            )}
                                            {tunerState.closestString && (
                                                <Text size="xs" c="dimmed">
                                                    Closest guitar string: {tunerState.closestString.name}
                                                </Text>
                                            )}
                                            <Badge
                                                color={getTunerColor()}
                                                variant="filled"
                                                size="lg"
                                            >
                                                {tunerState.isInTune ? 'In Tune!' : `${tunerState.centOffset > 0 ? '+' : ''}${tunerState.centOffset} cents`}
                                            </Badge>
                                        </Stack>
                                    ) : (
                                        <Text c="dimmed">Play a note to start tuning...</Text>
                                    )}
                                </Box>
                                
                                {tunerState.detectedNote && (
                                    <Box pos="relative" h={60}>
                                        <Box
                                            pos="absolute"
                                            top="50%"
                                            w="100%"
                                            h={4}
                                            bg="gray.3"
                                            style={{transform: 'translateY(-50%)', borderRadius: 2}}
                                        />
                                        <Box
                                            pos="absolute"
                                            top="50%"
                                            left="50%"
                                            w={2}
                                            h={30}
                                            bg="gray.6"
                                            style={{transform: 'translate(-50%, -50%)'}}
                                        />
                                        <Box
                                            pos="absolute"
                                            top="50%"
                                            left={`${getNeedlePosition()}%`}
                                            w={3}
                                            h={40}
                                            bg={getTunerColor()}
                                            style={{
                                                transform: 'translate(-50%, -50%)',
                                                transition: 'left 0.1s ease-out',
                                                borderRadius: 1
                                            }}
                                        />
                                        <Group justify="space-between" mt="sm">
                                            <Text size="xs" c="dimmed">Flat</Text>
                                            <Text size="xs" c="dimmed">Sharp</Text>
                                        </Group>
                                    </Box>
                                )}
                            </>
                        )}
                    </Stack>
                </Card>
                
                <Card padding="lg" radius="md" withBorder>
                    <Text size="md" fw={600} mb="md">Standard Guitar Tuning</Text>
                    <Stack gap="xs">
                        {GUITAR_STRINGS.map((string, index) => (
                            <Group key={string.name} justify="space-between">
                                <Text size="sm">
                                    String {string.string}: {string.name}
                                </Text>
                                <Text size="sm" c="dimmed">
                                    {string.frequency.toFixed(2)} Hz
                                </Text>
                            </Group>
                        ))}
                    </Stack>
                </Card>
        </Stack>
    );
}

springboard.registerModule('GuitarTuner', {}, async (moduleAPI): Promise<GuitarTunerModule> => {
    const uiEntrypoints = moduleAPI.getModule('UIEntrypoints');
    uiEntrypoints.settings.registerComponent({
        label: 'Guitar Tuner',
        icon: IconGauge,
        onClick: (modals) => {
            modals.openModal('CustomModal', {
                title: 'Guitar Tuner',
                form: TunerForm,
            });
        },
    });
    
    return {
        TunerForm,
    };
});